import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Paper,
  useTheme,
  Skeleton,
  Stack,
  Chip
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  Home,
  Favorite,
  AttachMoney,
  Assessment,
  ShowChart,
  AccountBalance
} from '@mui/icons-material';

// Import new components
import StatCard from './components/StatCard';
import ChartCard from './components/ChartCard';
import RecentActivityCard from './components/RecentActivityCard';
import QuickStatsCard from './components/QuickStatsCard';

// Services
import apiClient from '../../services/apiClient';
import { useFavorites } from '../../context/FavoritesContext';

const EnhancedDashboard = () => {
  const theme = useTheme();
  const { favorites } = useFavorites();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/properties/dashboard-analytics');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use mock data if API fails
      setDashboardData(getMockData());
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    overview: {
      totalProperties: 1247,
      averageValue: 285000,
      portfolioValue: 3550000,
      favoriteCount: favorites?.length || 0,
    },
    marketTrends: [
      { month: 'Jan', value: 245000, properties: 180 },
      { month: 'Feb', value: 258000, properties: 195 },
      { month: 'Mar', value: 272000, properties: 210 },
      { month: 'Apr', value: 280000, properties: 225 },
      { month: 'May', value: 285000, properties: 240 },
      { month: 'Jun', value: 290000, properties: 250 },
    ],
    propertyDistribution: [
      { name: 'Gloucester', value: 35 },
      { name: 'Bergen', value: 28 },
      { name: 'Hudson', value: 22 },
      { name: 'Others', value: 15 },
    ],
    performanceMetrics: [
      { label: 'Portfolio Growth', value: 85, color: 'primary' },
      { label: 'ROI Performance', value: 78, color: 'success' },
      { label: 'Market Analysis', value: 65, color: 'info' },
      { label: 'Risk Level', value: 42, color: 'warning' },
    ]
  });

  const data = dashboardData || getMockData();

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="700" gutterBottom>
              Dashboard Overview
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back! Here's your property investment summary
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
            >
              Refresh Data
            </Button>
            <Button
              variant="contained"
              startIcon={<Assessment />}
            >
              View Report
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Properties"
            value={data.overview?.totalProperties?.toLocaleString() || '0'}
            icon={Home}
            change={12.5}
            changeType="increase"
            color="primary"
            subtitle="Active listings"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Average Value"
            value={`$${(data.overview?.averageValue || 0).toLocaleString()}`}
            icon={AttachMoney}
            change={8.2}
            changeType="increase"
            color="success"
            subtitle="Per property"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Portfolio Value"
            value={`$${((data.overview?.portfolioValue || 0) / 1000000).toFixed(2)}M`}
            icon={AccountBalance}
            change={15.3}
            changeType="increase"
            color="info"
            subtitle="Total worth"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Favorites"
            value={data.overview?.favoriteCount || 0}
            icon={Favorite}
            change={23.1}
            changeType="increase"
            color="error"
            subtitle="Saved properties"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Market Trends"
            subtitle="Property values over the last 6 months"
            type="area"
            data={data.marketTrends || []}
            dataKey={['value', 'properties']}
            xAxisKey="month"
            height={350}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Property Distribution"
            subtitle="By county"
            type="pie"
            data={data.propertyDistribution || []}
            dataKey="value"
            height={350}
          />
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <RecentActivityCard />
        </Grid>
        <Grid item xs={12} md={4}>
          <QuickStatsCard stats={data.performanceMetrics} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default EnhancedDashboard;











